import { useEffect, useState } from "react";
import { MainPageBase } from "../page-bases/MainPageBase";
import {
  Avatar,
  Box,
  Divider,
  IconButton,
  Stack,
  Tooltip,
} from "@mui/material";
import { useParams } from "react-router-dom";

import { TRowItem } from "../../components/Table/TableRow";
import { useMangeTableItems } from "../../hooks/useManageTableItems";
import { usePagination } from "../../hooks/usePagination";
import { assetBatchDistributionApi } from "../../services/distribution/asset-distribution";
import { actionDialogStore, alertDialogStore } from "../../store/dialogs";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { SimpleLabel } from "../../components/Label/SimpleLabel";
import { updateBatchDistributionCart } from "../../store/reducers/distribution/distributedRecordStore";
import {
  AssetDistributionQueryParams,
  IAssetRecord,
  IPagination,
  IQueryPerams,
} from "../../config/types";
import { formatDate } from "../../utils/common";
import DamsTable from "../../components/Table/CollapsibleTable";
import { BadgeLabel } from "../../components/Label";
import { DamsAppAsset } from "../../assets/registry";

import LabelButton from "../../components/Button/LabelButton";
import useAppNavigation from "../../hooks/useAppNavigation";
import appRoutePath from "../../config/path";
import { ModalBase } from "../../components/Modals/ModalBase";

import ModalItemInput from "../../components/Input/ModalItemInput";
import { DistributedAssetsLogsFilter } from "../../common-ui/page-filters/DistributedAssetsLogsFilter";


function InBoundDistributionActionPage() {
  const [dataset, setDataset] = useState<TRowItem[]>([]);
  const { id } = useParams<{ id: string }>();
  const entryDetail = useAppSelector((data) => data.batchDistributionCart);
  const fetchAssetsFromDistribution = (query?: AssetDistributionQueryParams) =>
    assetBatchDistributionApi.getAssetFromDistribution(id ?? "", query);
  const {
    pagination,
    paginateToNextPage,
    paginateToPreviousPage,
    paginateToPageNumber,
    updatePaginationInitState,
  } = usePagination<any>(fetchAssetsFromDistribution);
  const dispatch = useAppDispatch();
  const { selectedRowItems, toggleRow, toggleSelectAllRow } =
    useMangeTableItems(dataset);
  const selectedAssetsIds = (selectedRowItems as IAssetRecord[]).map(
    (asset) => asset.id
  );

  const [showModel, setShowModel] = useState<{
    state: boolean;
    isbatch?: boolean;
    item?: TRowItem;
  }>({ state: false });

  const [rejectionReason, setRejectionReason] = useState("");
  const [querySet, setQuerySet] = useState<IQueryPerams>({ is_deleted: false });
  const populateDataSet = (data: IAssetRecord[], pagination?: IPagination) => {
    const formatData = data.map((data) => ({
      ...data,
      date_received: formatDate(data.date_received),
      brand: `${data.brand} (${data.model})`,
      current_location_domain: `${data.current_location_domain} (${data.current_location})`,
      asset_meta_raw: data.asset_meta
        ? data.asset_meta.map(
            (item) => ` ${item.asset_tag}=${item.meta_value} `
          )
        : [],
    }));

    setDataset(formatData);
    updatePaginationInitState(pagination);
  };

  const fetchData = async () => {
    const result = await assetBatchDistributionApi.getAssetFromDistribution(
      id ?? ""
    );
    if (result?.data) {
      populateDataSet(result.data, result.pagination);
    }
    const distributionDetail =
      await assetBatchDistributionApi.getBatchDistribution(id ?? "");
    if (distributionDetail.data) {
      dispatch(updateBatchDistributionCart(distributionDetail.data));
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const navigate = useAppNavigation();

  const openRejectionForm = (isBatch: boolean, item?: TRowItem) => {
    setShowModel({
      state: true,
      isbatch: isBatch,
      item,
    });
  };
  const closeRejectionForm = () => {
    setShowModel((p) => ({ ...p, state: false }));
    setRejectionReason("");
  };

  async function acceptRequest(asset_id?: string) {
    if (!id) {
      alertDialogStore.open({
        title: "Unexpected Error",
        subtitle: "Refresh page and try again",
        isSuccess: false,
        rightBtnTxt:'Okay'
      });
      return;
    }
    const assetsIds = asset_id !== undefined ? [asset_id] : selectedAssetsIds;
    if (assetsIds.length <= 0) {
      alertDialogStore.open({
        title: "Required Action",
        subtitle: "Please select assets to continue",
        isSuccess: false,
      });
      return;
    }
    const res = await assetBatchDistributionApi.accept(id, assetsIds);
    if (res.success) {
      fetchData();
    }
    alertDialogStore.open({
      title: res.success ? "Accept Asset" : "Accept Asset Failed!",
      subtitle: res.message,
      isSuccess: res.success,
      rightBtnTxt:'Okay'
    });
  }

  async function rejectRequest(asset_id?: string) {
    if (!id) {
      alertDialogStore.open({
        title: "Unexpected Error",
        subtitle: "Refresh page and try again",
        isSuccess: false,
      });
      return;
    }
    const assetsIds = asset_id !== undefined ? [asset_id] : selectedAssetsIds;
    const res = await assetBatchDistributionApi.reject(
      id,
      assetsIds,
      rejectionReason
    );
    if (res.success) {
      fetchData();
    }
    closeRejectionForm();
    alertDialogStore.open({
      title: res.success? "Asset Rejection" :"Asset Rejection Failed!",
      subtitle: res.message,
      isSuccess: res.success,
      rightBtnTxt:'Okay'
    });
   
  }

  const submitRejectionForm = () => {
    if (!rejectionReason) {
      alertDialogStore.open({
        title: "Action Required",
        subtitle: `Please provide reason for this rejection`,
        isSuccess: false,
        rightBtnTxt: "Okay",
      });

      return;
    }

    const record = showModel.item;
    toggleRejectButton(record);
  };

  if (!entryDetail.id) {
    return null;
  }

  function toggleAcceptButton(item?: TRowItem) {
    actionDialogStore.open({
      title: item ? "Accept Asset" : "Accept Assets",
      subtitle: item
        ? `${item.brand} ,model ${item.model} ,Click Accept to continue `
        : "Click Accept to continue",
      onRightBtnPress: async () => acceptRequest(item ? item.id : undefined),
    });
  }

  function toggleRejectButton(item?: TRowItem) {
    actionDialogStore.open({
      title: item ? "Asset Rejection" : "Assets Rejection",
      subtitle: item
        ? `${item.brand} ,model ${item.model} ,Click reject to continue `
        : "Click reject to continue",
      onRightBtnPress: async () => rejectRequest(item?.id),
    });
  }

  const renderTableActions = ({ item }: { item: TRowItem }) => {
    const record = item as IAssetRecord;

    const status = record.distribution_status;

    const showButtons = status === "in-transit";
    if (showButtons === false) {
      return (
        <>
          {status === "received" ? (
            <BadgeLabel title={status.toUpperCase()} colorType="green" />
          ) : (
            <BadgeLabel title={status.toUpperCase()} colorType="red" />
          )}
        </>
      );
    }
    return (
      <Stack direction={"row"} spacing={2}>
        <BadgeLabel
          onClick={() => toggleAcceptButton(item)}
          title={"Accept"}
          colorType="green"
          tip="Click to accept asset"
        />
        <BadgeLabel
          onClick={() => openRejectionForm(false, item)}
          title={"Reject"}
          colorType="red"
          tip="Click to reject asset"
        />
      </Stack>
    );
  };

  const renderHeaderDropDownMenu = ({
    closeDialog,
  }: {
    closeDialog: () => void;
  }) => {
    return (
      <Box sx={{ overflow: "hidden" }}>
        <Tooltip
          title={`Click to accept selected assets . Total selected ${selectedRowItems.length} `}
        >
          <IconButton
            color="success"
            sx={{
              padding: 0,
              borderRadius: 0,
            }}
            onClick={() => toggleAcceptButton(undefined)}
          >
            <Stack
              direction={"row"}
              justifyContent={"space-between"}
              alignItems={"center"}
              py={1}
              pl={1}
              spacing={3}
            >
              <Avatar
                src={DamsAppAsset.icons.roles_green}
                sx={{ width: 20, height: 20 }}
              />
              <SimpleLabel title="Accept  assets" />
            </Stack>
          </IconButton>
        </Tooltip>

        <Divider />
        <Tooltip
          title={`Click to reject selected assets . Total selected ${selectedRowItems.length} `}
        >
          <IconButton
            color="warning"
            sx={{ padding: 0, borderRadius: 0 }}
            onClick={() => openRejectionForm(true)}
          >
            <Stack
              direction={"row"}
              justifyContent={"space-between"}
              alignItems={"center"}
              py={1}
              pl={1}
              spacing={3}
            >
              <Avatar
                src={DamsAppAsset.icons.close}
                sx={{ width: 20, height: 20 }}
              />
              <SimpleLabel title="Reject assets" />
            </Stack>
          </IconButton>
        </Tooltip>
      </Box>
    );
  };

  return (
    <MainPageBase
      pageState={"main"}
      title={"Inspect Inbound Distribution"}
      subtitle={`Review assets received from another domain and take action on each item.`}
      extraComponent={
        <LabelButton
          onPress={() => {
            navigate({ path: appRoutePath.INBOUND_ASSET_DISTRIBUTIONS_PAGE });
          }}
          label={"Back"}
          direction={"left"}
        />
      }
    >
      <ModalBase
        open={showModel.state}
        title={"Reject Distribution Asset"}
        subtitle={""}
        type={"add-item"}
        showHeader={true}
        leftIcon={DamsAppAsset.icons.warning}
        leftBtnTxt="Cancel"
        onLeftBtnPress={closeRejectionForm}
        rightBtnTxt={"Reject"}
        onRightBtnPress={() => submitRejectionForm()}
        onClose={closeRejectionForm}
      >
        <Stack gap={2}>
          <ModalItemInput
            label="Reason"
            placeholder="Enter reason"
            handleChange={(e) => setRejectionReason(e.target.value)}
            value={rejectionReason}
            errorMessage={"Please provide reason for rejection "}
            multiline
            rows={5}
          />
        </Stack>
      </ModalBase>

      <Box height={`100%`} display={"flex"} justifyContent={"center"}>
        <Stack width="100%">
          <Stack spacing={1.5} mb={2} direction="column">
            <SimpleLabel title="Departure Details" />

            <Stack direction="row" width="100%">
              <SimpleLabel title="Departure Domain" fontWeight={400} />
              <SimpleLabel
                title={entryDetail.from_location_domain}
                fontWeight={700}
                width="100%"
              />
            </Stack>

            <Stack direction="row" width="100%">
              <SimpleLabel title="Departure Location" fontWeight={400} />
              <SimpleLabel
                title={entryDetail.from_location}
                fontWeight={700}
                width="100%"
              />
            </Stack>

            <Divider />
            <SimpleLabel title="Recipient Details" />

            <Stack direction="row" width="100%">
              <SimpleLabel title="Recipient Domain" fontWeight={400} />
              <SimpleLabel
                title={entryDetail.to_location_domain}
                fontWeight={700}
                width="100%"
              />
            </Stack>

            <Stack direction="row" width="100%">
              <SimpleLabel title="Recipient Destination" fontWeight={400} />
              <SimpleLabel
                title={entryDetail.to_location}
                fontWeight={700}
                width="100%"
              />
            </Stack>
          </Stack>

          <DistributedAssetsLogsFilter<IAssetRecord[]>
            ftn={fetchAssetsFromDistribution}
            updateDataSet={populateDataSet}
            updateQuerySet={setQuerySet}
            isAssigned={false}
            isDeleted={false}
          />

          <DamsTable
            tableTitle="Assets for Inspection"
            tableSubtitle="Inspect each asset and choose to accept or reject from this distribution."
            tableTitleBadge={
              selectedRowItems.length > 0
                ? `${selectedRowItems.length} selected`
                : ""
            }
            tableHeaders={columns}
            hiddenKeys={hkeys}
            hiddenLabels={hcolumns}
            data={dataset}
            tableKeys={tkeys}
            useTableCheckbox
            selectedRowItems={selectedRowItems}
            onTableItemSelect={toggleRow}
            toggleSelectAllRow={toggleSelectAllRow}
            hasPagination
            paginationState={pagination}
            onNextPage={paginateToNextPage(querySet, populateDataSet)}
            onPreviousPage={paginateToPreviousPage(querySet, populateDataSet)}
            setActivePage={paginateToPageNumber(populateDataSet, querySet)}
            extraRowComponent={renderTableActions}
            headerDropDownMenu={renderHeaderDropDownMenu}
          />
        </Stack>
      </Box>
    </MainPageBase>
  );
}

const tkeys = [
  "brand",
  "serial_number",
  "embossment_number",
  "distribution_status",
  "assigned",
  "date_received",
];

const columns = [
  "Brand (Model)",
  "Serial Number",
  "Embossment Number",
  "Distribution Status",
  "Assigned",
  "Date Received",
];
const hkeys = [
  "asset_category_name",
  "warranty_in_months",
  "batch_number",
  "current_location_domain",
  "asset_meta_raw",
];

const hcolumns = [
  "Asset Category",
  "Warranty (months)",
  "Batch Number",
  "Current Location - Domain (Location)",
  "Additional Information",
];
export { InBoundDistributionActionPage };
